import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import { Upload, X, Eye, SquarePen, Download } from "lucide-react";

import { get, post } from "../utils/api";
import { toast } from "react-toastify";

const SalesListing = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Filter State
  const [distributorFilter, setDistributorFilter] = useState("");
  const [salesHeadStatusFilter, setSalesHeadStatusFilter] = useState("");
  const [agreementStatusFilter, setAgreementStatusFilter] = useState("");

  // Upload Modal State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadRow, setUploadRow] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
        const salesName = userDetails?.name || userDetails?.username || "";

        const response = await post("/sales_listing/fetchDistributorsSalesMapping", {
          sales_name: salesName,
        });
        if (response.data?.Success) {
          const mappedData = response.data.Data.map(item => ({
            distributor_name: item.distributor_name,
            arn_code: item.arn_code,
            email: item.distributor_email,
            sales_head_approval: item.sales_head_status || "Pending",
            agreement_status: item.agreement_status || "Pending",
            rejected_reason: item.rejection_reason || "-",
            distributor_sequence_number: item.distributor_sequence_number, // Added for View details
          }));
          setData(mappedData);
        } else {
          toast.error(response.data?.Message || "Failed to fetch distributors");
        }
      } catch (error) {
        console.error("Fetch error:", error);
        toast.error("Error fetching distributors");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDownload = async (row) => {
    try {
      if (!row.distributor_sequence_number) {
        toast.error("Distributor Sequence Number missing for this entry.");
        return;
      }

      toast.info("Generating and downloading Word agreement...");

      // Use POST request to match backend route for DOCX
      const response = await post("/fileGeneration/downloadAgreementDocx",
        { distributor_sequence_number: row.distributor_sequence_number }
      );

      if (response.data && response.data.Success && response.data.Data) {
        // Convert Base64 to Blob
        const byteCharacters = atob(response.data.Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Agreement_${row.distributor_name || "Document"}.docx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Download started successfully");
      } else {
        console.error("Download response error:", response.data);
        toast.error(response.data?.Message || "Failed to generate agreement");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(error.response?.data?.Message || "Failed to download agreement.");
    }
  };

  // Handle Upload Click (Open Modal)
  const handleUploadClick = (row) => {
    setUploadRow(row);
    setUploadFile(null);
    setIsUploadModalOpen(true);
  };

  // Handle Upload Submit (API Call)
  const handleUploadSubmit = async () => {
    if (!uploadFile || !uploadRow) return;

    try {
      setModalLoading(true);
      const formData = new FormData();
      formData.append("distributor_sequence_number", uploadRow.distributor_sequence_number);
      formData.append("file", uploadFile);

      // Use the generic 'post' helper but we need to handle FormData headers correctly or let axios do it
      // The 'post' helper might set JSON headers. Let's try passing config if supported, or rely on axios auto-detection if body is FormData
      // Checking api.js: it uses `api.post(url, data)`. Axios usually auto-sets Content-Type for FormData.

      const response = await post("/fileGeneration/uploadSignedAgreement", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        }
      });

      if (response.data && response.data.Success) {
        toast.success("Agreement uploaded and converted successfully!");
        setIsUploadModalOpen(false);
        setUploadFile(null);
        setUploadRow(null);
      } else {
        toast.error(response.data?.Message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload agreement");
    } finally {
      setModalLoading(false);
    }
  };

  const handleView = async (row) => {
    setIsModalOpen(true);
    setModalLoading(true);
    setSelectedDistributor(null);

    try {
      // Use the sequence number if available, otherwise fallback to ID or handle error
      console.log("View Row Data:", row); // Debug log
      const seqNum = row.distributor_sequence_number;
      console.log("Sequence Number:", seqNum); // Debug log

      if (!seqNum) {
        toast.error("No Sequence Number found for this entry.");
        setModalLoading(false);
        return;
      }

      const response = await post("/sales_listing/fetchDistributorWithProducts", {
        distributor_sequence_number: seqNum
      });
      console.log("View API Response:", response); // Debug log
      if (response.data?.Success) {
        const sortedData = (response.data.Data || []).sort((a, b) => {
          const classA = a.class_code || a.class || "";
          const classB = b.class_code || b.class || "";
          const classCompare = classA.localeCompare(classB, undefined, { numeric: true, sensitivity: "base" });
          if (classCompare !== 0) return classCompare;
          const fundA = a.fund_code || a.fund_name || "";
          const fundB = b.fund_code || b.fund_name || "";
          return fundA.localeCompare(fundB, undefined, { numeric: true, sensitivity: "base" });
        });
        setSelectedDistributor(sortedData);
      } else {
        toast.error("Failed to fetch details.");
      }
    } catch (error) {
      console.error("View Fetch Error:", error);
      toast.error("Error fetching details.");
    } finally {
      setModalLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Distributor Name",
        accessor: "distributor_name",
        size: 200,
        Cell: ({ value }) => <div className="font-medium text-gray-900">{value}</div>,
      },
      {
        Header: "APRN Code",
        accessor: "arn_code",
        size: 200,
      },
      {
        Header: "Email ID",
        accessor: "email",
        size: 250,
      },
      {
        Header: "Sales Head Approval Status",
        accessor: "sales_head_approval",
        size: 280,
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${value === "Approved"
              ? "bg-green-100 text-green-700"
              : value === "Pending"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-red-100 text-red-700"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Agreement Status",
        accessor: "agreement_status",
        size: 280,
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${value === "Signed"
              ? "bg-green-100 text-green-700"
              : value?.startsWith("Rejected")
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Rejected Reason",
        accessor: "rejected_reason",
        size: 220,
        Cell: ({ value }) => <div className="whitespace-normal break-words" title={value}>{value}</div>,
      },
      {
        Header: "ACTION",
        accessor: "action",
        size: 320,
        Cell: ({ row }) => {
          const isRejected = row.original.agreement_status?.startsWith("Rejected");

          return (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => handleView(row.original)}
                className="bg-[#F1F4F9] border border-[#E2E8F0] text-gray-800 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-gray-200 text-[11px] font-semibold"
                title="View Details"
              >
                <Eye size={14} />
                View
              </button>

              {isRejected && (
                <>
                  <button
                    onClick={() => navigate("/commercial_dashboard", { state: { distributorData: row.original } })}
                    className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-blue-100 text-[11px] font-semibold"
                    title="Edit"
                  >
                    <SquarePen size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDownload(row.original)}
                    className="bg-orange-50 border border-orange-200 text-orange-700 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-orange-100 text-[11px] font-semibold"
                    title="Download Agreement"
                  >
                    <Download size={14} />
                    Download
                  </button>
                  <button
                    onClick={() => handleUploadClick(row.original)}
                    className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 hover:bg-emerald-100 text-[11px] font-semibold"
                    title="Upload Agreement"
                  >
                    <Upload size={14} />
                    Upload
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const distributorOptions = useMemo(
    () => [...new Set(data.map((item) => item.distributor_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [data]
  );
  const salesHeadStatusOptions = useMemo(
    () => [...new Set(data.map((item) => item.sales_head_approval).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [data]
  );
  const agreementStatusOptions = useMemo(
    () => [...new Set(data.map((item) => item.agreement_status).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [data]
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesDistributor = distributorFilter ? item.distributor_name === distributorFilter : true;
      const matchesSalesHeadStatus = salesHeadStatusFilter ? item.sales_head_approval === salesHeadStatusFilter : true;
      const matchesAgreementStatus = agreementStatusFilter ? item.agreement_status === agreementStatusFilter : true;
      return matchesDistributor && matchesSalesHeadStatus && matchesAgreementStatus;
    });
  }, [data, distributorFilter, salesHeadStatusFilter, agreementStatusFilter]);

  return (
    <div className="max-w-screen-xl mx-auto p-4 space-y-6">
      <div className="bg-white p-4 rounded shadow space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-2">
          <h1 className="text-xl font-bold text-gray-800 tracking-tight">Sales Listing</h1>

          {/* Filter Section */}
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            <select
              value={distributorFilter}
              onChange={(e) => setDistributorFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Distributors</option>
              {distributorOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>

            <select
              value={salesHeadStatusFilter}
              onChange={(e) => setSalesHeadStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Sales Head Status</option>
              {salesHeadStatusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            <select
              value={agreementStatusFilter}
              onChange={(e) => setAgreementStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Agreement Status</option>
              {agreementStatusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
        <ReusableTable columns={columns} data={filteredData} />
      </div>

      {/* View Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold">Commercial Details</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-red-500"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {modalLoading ? (
                <div className="flex justify-center py-10">
                  <span className="text-gray-500">Loading details...</span>
                </div>
              ) : selectedDistributor && selectedDistributor.length > 0 ? (
                <>
                  {/* Distributor Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-md">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Distributor Name</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].distributor_name || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">APRN Code</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].arn_code || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Email</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].distributor_email || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">PAN Number</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].pan_number || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Address</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].address || selectedDistributor[0].distributor_address || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Pin Code</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].pincode || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">City</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].city || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">State</label>
                      <p className="text-gray-900 font-medium">{selectedDistributor[0].state || "-"}</p>
                    </div>
                  </div>

                  {/* Fund Details Tables */}
                  <div>
                    <h3 className="font-bold text-md mb-3 border-b pb-1">Fund Details</h3>

                    {/* AIF CAT II */}
                    {selectedDistributor.filter(d => d.product_type_id === 1).length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-sm mb-2 text-purple-800">AIF CAT II</h4>
                        <ReusableTable
                          data={selectedDistributor.filter(d => d.product_type_id === 1)}
                          columns={[
                            { Header: "Fund Name", accessor: "fund_name", size: 250, Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div> },
                            { Header: "Class", accessor: "class_code", size: 150, Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div> },
                            { Header: "Contribution (In CR)", accessor: "size", size: 180, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Mgmt Fee %", accessor: "management_fee_sharing_per", size: 150, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Distributor Sharing %", accessor: "placement_fee_per", size: 220, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Upfront Fee %", accessor: "upfront_fee_per", size: 180, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Trail Fee %", accessor: "trail_fee_per", size: 150, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                          ]}
                        />
                      </div>
                    )}

                    {/* AIF CAT III */}
                    {selectedDistributor.filter(d => d.product_type_id === 2).length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-sm mb-2 text-purple-800">AIF CAT III</h4>
                        <ReusableTable
                          data={selectedDistributor.filter(d => d.product_type_id === 2)}
                          columns={[
                            { Header: "Fund Name", accessor: "fund_name", size: 300, Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div> },
                            { Header: "Class", accessor: "class_code", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div> },
                            { Header: "Mgmt Fee %", accessor: "management_fee_sharing_per", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Dist. Sharing Fee %", accessor: "performance_fee_sharing_per", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                          ]}
                        />
                      </div>
                    )}

                    {/* PMS */}
                    {selectedDistributor.filter(d => d.product_type_id === 3).length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-sm mb-2 text-purple-800">PMS</h4>
                        <ReusableTable
                          data={selectedDistributor.filter(d => d.product_type_id === 3)}
                          columns={[
                            { Header: "Fund Name", accessor: "fund_name", size: 300, Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div> },
                            { Header: "Class", accessor: "class_code", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div> },
                            { Header: "Mgmt Fee < 15Cr %", accessor: "management_fee_sharing_per", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Mgmt Fee > 15Cr %", accessor: "management_fee_sharing_per_morethan15cr", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> },
                            { Header: "Dist. Sharing Fee %", accessor: "performance_fee_sharing_per", size: 200, Cell: ({ value }) => <div className="px-2 py-1">{value || "-"}</div> }, // Assuming perf fee maps to sharing for PMS or checking api
                          ]}
                        />
                      </div>
                    )}

                    {/* Fallback if no funds */}
                    {!selectedDistributor.some(d => [1, 2, 3].includes(d.product_type_id)) && (
                      <p className="text-gray-500 italic">No fund details found.</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-10 text-red-500">Failed to load details.</div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Upload Agreement Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Upload Signed Agreement</h2>
              <button
                onClick={() => { setIsUploadModalOpen(false); setUploadFile(null); }}
                className="text-gray-500 hover:text-red-500"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload the modified Word document (.docx). This will replace the existing agreement.
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors">
                <input
                  type="file"
                  accept=".doc,.docx"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="hidden"
                  id="agreement-upload"
                />
                <label htmlFor="agreement-upload" className="cursor-pointer block">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-900">
                    {uploadFile ? uploadFile.name : "Click to select file"}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">DOC, DOCX up to 10MB</p>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="cancel"
                  onClick={() => { setIsUploadModalOpen(false); setUploadFile(null); }}
                  disabled={modalLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadSubmit}
                  disabled={!uploadFile || modalLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {modalLoading ? "Uploading..." : "Upload & Convert"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesListing;
